import { CommonModule } from "@angular/common";
import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    inject,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import {
    EzTerminologyHealthDiagnostic,
    EzTerminologyHealthReport,
    EzTerminologyUsageHealthEntry,
    PlatformConfigurationDiagnosticResult,
} from "@app/platform-admin/configuration-diagnostics/platform-configuration-diagnostics.models";
import { PlatformConfigurationDiagnosticsService } from "@app/platform-admin/configuration-diagnostics/platform-configuration-diagnostics.service";
import {
    TerminologyVocabularyDefinitionDetail,
    TerminologyVocabularyPublicationHistoryItem,
    TerminologyVocabularyRevisionHistoryItem,
} from "./platform-terminology-admin.models";

type HistoryHealthTab = "history" | "health";
type HistoryActionFilter = "" | "publish" | "restore" | "retired";
type HealthEvidenceScope = "selected" | "configuration";

@Component({
    selector: "phms-platform-terminology-history-health-panel",
    imports: [CommonModule, FormsModule],
    templateUrl: "./platform-terminology-history-health-panel.component.html",
    styleUrl: "./platform-terminology-history-health-panel.component.scss",
})
export class PlatformTerminologyHistoryHealthPanelComponent implements OnChanges {
    private readonly diagnosticsService = inject(PlatformConfigurationDiagnosticsService);
    private healthRequestId = 0;
    private readonly destroyRef = inject(DestroyRef);

    @Input({ required: true }) detail!: TerminologyVocabularyDefinitionDetail;
    @Input() history: TerminologyVocabularyRevisionHistoryItem[] = [];
    @Output() readonly historyRefreshRequested = new EventEmitter<void>();

    readonly isHealthLoading = signal(false);
    readonly healthResult = signal<PlatformConfigurationDiagnosticResult<EzTerminologyHealthReport> | null>(null);

    activeTab: HistoryHealthTab = "history";
    selectedRevisionId: number | null = null;
    historySearch = "";
    historyActionFilter: HistoryActionFilter = "";
    diagnosticScope: HealthEvidenceScope = "selected";
    usageScope: HealthEvidenceScope = "selected";
    healthSearch = "";

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["detail"] && this.detail) {
            this.healthRequestId += 1;
            this.isHealthLoading.set(false);
            this.selectedRevisionId = this.definition.activeRevisionId ?? null;
            this.healthResult.set(null);
            if (this.activeTab === "health") this.loadHealth();
        }

        if (changes["history"]) {
            this.ensureRevisionSelection();
        }
    }

    get definition() {
        return this.detail.definition;
    }

    get filteredHistory(): TerminologyVocabularyRevisionHistoryItem[] {
        const search = this.historySearch.trim().toLowerCase();
        return [...this.history]
            .filter((revision) => {
                const matchesAction = !this.historyActionFilter
                    || (this.historyActionFilter === "retired"
                        ? revision.publications.some((publication) => publication.retiredAtUtc != null)
                        : revision.publications.some((publication) =>
                            publication.actionCode.toLowerCase() === this.historyActionFilter));
                if (!matchesAction || !search) {
                    return matchesAction;
                }
                return [
                    `revision ${revision.revisionNumber}`,
                    revision.revisionId.toString(),
                    revision.contentText,
                    revision.changeReason,
                    revision.authorUserId.toString(),
                    ...revision.publications.flatMap((publication) => [
                        publication.publicationId.toString(),
                        publication.actionCode,
                        publication.statusCode,
                        publication.reason,
                        publication.publisherUserId.toString(),
                        publication.retirementReason,
                        publication.correlationId,
                    ]),
                ].some((value) => value?.toLowerCase().includes(search));
            })
            .sort((left, right) => right.revisionNumber - left.revisionNumber);
    }

    get selectedRevision(): TerminologyVocabularyRevisionHistoryItem | null {
        return this.history.find((revision) => revision.revisionId === this.selectedRevisionId) ?? null;
    }

    get selectedPublications(): TerminologyVocabularyPublicationHistoryItem[] {
        return [...(this.selectedRevision?.publications ?? [])]
            .sort((left, right) => Date.parse(right.publishedAtUtc) - Date.parse(left.publishedAtUtc));
    }

    get publicationEventCount(): number {
        return this.history.reduce((total, revision) => total + revision.publications.length, 0);
    }

    get restoreEventCount(): number {
        return this.history.reduce((total, revision) => total
            + revision.publications.filter((publication) => publication.actionCode.toLowerCase() === "restore").length, 0);
    }

    get retirementEventCount(): number {
        return this.history.reduce((total, revision) => total
            + revision.publications.filter((publication) => publication.retiredAtUtc != null).length, 0);
    }

    get activePublicationCount(): number {
        return this.history.reduce((total, revision) => total
            + revision.publications.filter((publication) => publication.isCurrentlyEffective).length, 0);
    }

    get healthData(): EzTerminologyHealthReport | null {
        return this.healthResult()?.data ?? null;
    }

    get contextMatchesSelection(): boolean {
        const healthConfigurationVersionId = this.healthData?.configuration.configurationVersionId;
        const selectedConfigurationVersionId = this.definition.configurationVersionId ?? this.definition.initialWording?.configurationVersionId;
        return healthConfigurationVersionId != null
            && selectedConfigurationVersionId != null
            && healthConfigurationVersionId === selectedConfigurationVersionId;
    }

    get selectedTokenDiagnostics(): EzTerminologyHealthDiagnostic[] {
        const tokenCode = this.definition.tokenCode.toLowerCase();
        return (this.healthData?.diagnostics ?? []).filter((diagnostic) =>
            diagnostic.tokenCode.toLowerCase() === tokenCode
            || diagnostic.relatedTokenCode?.toLowerCase() === tokenCode
            || diagnostic.dependencyPath?.some((token) => token.toLowerCase() === tokenCode));
    }

    get visibleDiagnostics(): EzTerminologyHealthDiagnostic[] {
        const diagnostics = this.diagnosticScope === "selected"
            ? this.selectedTokenDiagnostics
            : (this.healthData?.diagnostics ?? []);
        const search = this.healthSearch.trim().toLowerCase();
        return diagnostics.filter((diagnostic) => !search || [
            diagnostic.severityCode,
            diagnostic.reasonCode,
            diagnostic.tokenCode,
            diagnostic.relatedTokenCode,
            diagnostic.dependencyPath?.join(" "),
        ].some((value) => value?.toLowerCase().includes(search)));
    }

    get selectedTokenUsage(): EzTerminologyUsageHealthEntry | null {
        const tokenCode = this.definition.tokenCode.toLowerCase();
        return this.healthData?.usageEvidence?.entries
            .find((entry) => entry.tokenCode.toLowerCase() === tokenCode) ?? null;
    }

    get visibleUsageEntries(): EzTerminologyUsageHealthEntry[] {
        const entries = this.healthData?.usageEvidence?.entries ?? [];
        const scopedEntries = this.usageScope === "selected"
            ? entries.filter((entry) => entry.tokenCode.toLowerCase() === this.definition.tokenCode.toLowerCase())
            : entries;
        const search = this.healthSearch.trim().toLowerCase();
        return scopedEntries
            .filter((entry) => !search || [entry.tokenCode, entry.statusCode]
                .some((value) => value.toLowerCase().includes(search)))
            .sort((left, right) => left.tokenCode.localeCompare(right.tokenCode));
    }

    get selectedTokenHealthLabel(): string {
        const result = this.healthResult();
        if (this.isHealthLoading()) {
            return "Checking";
        }
        if (!result || result.state !== "resolved" || !result.data) {
            return result ? "Unavailable" : "Not checked";
        }
        if (!this.contextMatchesSelection) {
            return "Context mismatch";
        }
        if (!result.data.evaluatedTokenCodes?.some((token) =>
            token.toLowerCase() === this.definition.tokenCode.toLowerCase())) {
            return "Not evaluated";
        }
        if (this.selectedTokenDiagnostics.some((diagnostic) =>
            diagnostic.severityCode.toLowerCase() === "error")) {
            return "Error";
        }
        if (this.selectedTokenDiagnostics.length > 0) {
            return "Warning";
        }
        return "Healthy";
    }

    get selectedTokenHealthDetail(): string {
        switch (this.selectedTokenHealthLabel) {
            case "Healthy":
                return "This token resolves without diagnostics in the selected runtime context.";
            case "Warning":
            case "Error":
                return `${this.selectedTokenDiagnostics.length} runtime diagnostic${this.selectedTokenDiagnostics.length === 1 ? "" : "s"} involve this token.`;
            case "Not evaluated":
                return "The resolved configuration did not contain this token. Review scope and publication state.";
            case "Context mismatch":
                return "The health endpoint resolved a different configuration version; its token result is not attributed to this editor selection.";
            case "Checking":
                return "Resolution health is loading.";
            case "Not checked":
                return "Open Resolution health or refresh evidence to evaluate the selected runtime context.";
            default:
                return this.healthResult()?.error?.messages.join(" ") ?? "Resolution health could not be loaded.";
        }
    }

    selectTab(tab: HistoryHealthTab): void {
        this.activeTab = tab;
        if (tab === "health" && !this.healthResult() && !this.isHealthLoading()) {
            this.loadHealth();
        }
    }

    selectRevision(revisionId: number): void {
        this.selectedRevisionId = revisionId;
    }

    refreshAll(): void {
        this.historyRefreshRequested.emit();
        this.loadHealth();
    }

    refreshHealth(): void {
        this.loadHealth();
    }

    revisionState(revision: TerminologyVocabularyRevisionHistoryItem): string {
        if (revision.publications.some((publication) => publication.isCurrentlyEffective)) {
            return "active";
        }
        if (revision.publications.some((publication) => publication.retiredAtUtc != null)) {
            return "retired";
        }
        if (revision.publications.some((publication) => publication.actionCode.toLowerCase() === "restore")) {
            return "restored";
        }
        return revision.publications.length > 0 ? "historical" : "unpublished";
    }

    revisionStateLabel(revision: TerminologyVocabularyRevisionHistoryItem): string {
        const state = this.revisionState(revision);
        return state.charAt(0).toUpperCase() + state.slice(1);
    }

    formatDateTime(value: string | null | undefined): string {
        if (!value) {
            return "Not recorded";
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime())
            ? value
            : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
    }

    formatAge(value: string | null | undefined): string {
        if (!value) {
            return "Unknown";
        }
        const parsed = Date.parse(value);
        if (!Number.isFinite(parsed)) {
            return "Unknown";
        }
        const seconds = Math.max(0, Math.floor((Date.now() - parsed) / 1_000));
        if (seconds < 60) {
            return `${seconds}s ago`;
        }
        if (seconds < 3_600) {
            return `${Math.floor(seconds / 60)}m ago`;
        }
        return `${Math.floor(seconds / 3_600)}h ago`;
    }

    shortHash(value: string): string {
        return value.length > 14 ? `${value.slice(0, 14)}…` : value;
    }

    publicationWindow(publication: TerminologyVocabularyPublicationHistoryItem): string {
        return `${this.formatDateTime(publication.effectiveStartUtc)} → ${publication.effectiveEndUtc
            ? this.formatDateTime(publication.effectiveEndUtc)
            : "Open ended"}`;
    }

    assuranceLabel(publication: TerminologyVocabularyPublicationHistoryItem): string {
        if (!publication.assuranceMethodCode) {
            return "Not recorded";
        }
        return `${publication.assuranceMethodCode} · ${this.formatDateTime(publication.assuranceCapturedUtc)}`;
    }

    private ensureRevisionSelection(): void {
        if (this.selectedRevisionId != null
            && this.history.some((revision) => revision.revisionId === this.selectedRevisionId)) {
            return;
        }
        this.selectedRevisionId = this.history.find((revision) =>
            revision.revisionId === this.definition.activeRevisionId)?.revisionId
            ?? [...this.history].sort((left, right) => right.revisionNumber - left.revisionNumber)[0]?.revisionId
            ?? null;
    }

    private loadHealth(): void {
        const requestId = ++this.healthRequestId;
        this.isHealthLoading.set(true);
        this.diagnosticsService.getTerminologyHealth({
            configurationCode: this.definition.configurationCode,
            localeCode: this.definition.localeCode,
            channelCode: normalizeOptional(this.definition.channelCode),
            audienceCode: normalizeOptional(this.definition.audienceCode),
            tenantId: normalizeOptional(this.definition.tenantId),
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
            if (requestId !== this.healthRequestId) {
                return;
            }
            this.isHealthLoading.set(false);
            this.healthResult.set(result);
        });
    }
}

function normalizeOptional(value: string | null | undefined): string | undefined {
    const normalized = value?.trim() ?? "";
    return normalized || undefined;
}
